import {
  useState,
  useEffect,
  useRef,
  MutableRefObject,
  RefObject,
} from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  NumberInput,
  RadioButtonGroup,
  RadioButton,
  Tooltip,
  ModalWrapper,
} from 'carbon-components-react';
import messages from '../locales/messages';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';

import './DatabandRoiEstimator.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import countryList from '../services/currencies.json';
import { segmentTracking } from '../lib/tracker';
import Footnote from './Footnote/Footnote';
import Thanks from './Thanks/Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { usePrice } from '../hooks/usePrice';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';
import {
  UT30Databand,
  Max_Data_Engineers,
  Max_Mean_Time_To_Detect,
  Max_Mean_Time_To_Resolve,
} from '../constants/spread'; // 5000
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import * as URX_FORM from '../constants/urx-form';
import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import { FormModal } from 'src/common/utils/FormModal';
import { useDatabandSelector } from '../lib/redux/hooks';
import {
  convertToString,
  createUseTranslation,
} from '../../../common/hooks/widgets/useTranslation';

export interface DatabandRoiEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const DatabandRoiEstimator = ({
  langCode,
  test,
  countryToTest,
}: DatabandRoiEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const dataEngineersRef = useRef() as MutableRefObject<HTMLInputElement>;
  const focusRef = useRef() as RefObject<RadioButtonGroup>;
  // lang and userlocale is based out of ddo
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);
  const [environment] = useEnv();
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const [dataEngineers, setDataEngineers] = useState(80);
  const [invalidDataEngineers, setInvalidDataEngineers] = useState(false);
  const [invalidMeanTimeToDetect, setInvalidMeanTimeToDetect] = useState(false);
  const [invalidMeanTimeToResolve, setInvalidMeanTimeToResolve] =
    useState(false);
  const [meanTimeToDetect, setMeanTimeToDetect] = useState(5);
  const [meanTimeToResolve, setMeanTimeToResolve] = useState(10);
  const [focusLevel, setFocusLevel] = useState('Low');
  const [hoursSaving, setHoursSaving] = useState(100);
  const [yearlySaving, setYearlySaving] = useCurrency(currency, userLocale, 0);
  const [threeYearSaving, setThreeYearSaving] = useCurrency(
    currency,
    userLocale,
    0,
  );
  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isSmall = useSmallBreakpoint('560px');
  const { bpName, logo, companyId, desc, mssp } = useBPName(UT30Databand, true);
  const cookieURL = useCookiesURL();
  const [countDownPassed, setCountDownPassed] = useState(false);

  usePrice({
    dataEngineers,
    meanTimeToDetect,
    meanTimeToResolve,
    focusLevel,
    setHoursSaving,
    setYearlySaving,
    setThreeYearSaving,
  });

  const changeSelection = (e: string) => {
    setFocusLevel(String(e));
    segmentTracking('CTA Clicked', {
      pageTitle: 'Databand ROI Page',
      object: 'Focus level radio button',
      resultValue: 'CTA clicked',
      CTA: e + ' focus level',
      location: 'Databand ROI Estimator',
      action: '',
      field: '',
    });
  };

  useEffect(() => {
    setTimeout(() => {
      setCountDownPassed(!countDownPassed);
    }, 1000);
  }, []);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
  };

  useEffect(() => {
    setCookie();
  }, [
    dataEngineers,
    meanTimeToDetect,
    meanTimeToResolve,
    hoursSaving,
    yearlySaving,
    threeYearSaving,
  ]);

  const AEMMessages = useDatabandSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Number of data engineers:' +
            dataEngineers +
            ' | % time engrs spend finding issues:' +
            meanTimeToDetect +
            ' |  % time engrs spend resolving issues:' +
            meanTimeToResolve +
            ' | Focus level on data initiatives:' +
            focusLevel +
            ' | Hours saved yearly: ' +
            hoursSaving +
            ' | Estimated yearly saving:' +
            yearlySaving +
            ' | Estimated three year saving:' +
            threeYearSaving +
            (getBp() ? `|Preferred BP:${getBp()}` : ''),
          Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
          Q_ASSET_NAME: 'Databand ROI Estiamtor', // ISC - Asset Name
        },
      }),
    );

    console.log('cookie', Cookies.get('urxdcq'));
  };

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  const replaceBpField = () => {
    const bpField = document.getElementById('Q_BPNAME') as HTMLInputElement;

    if (bpName && bpField) {
      //update label text
      const bpLabel = bpField.labels?.length
        ? bpField.labels[0]
        : new HTMLInputElement();
      bpLabel.innerHTML = convertToString(useTranslation('preferred_bp', true));

      //replace the bp field by the bp name
      const bpText = document.createElement('p') as HTMLInputElement;
      bpText.innerText = bpName;
      bpText.style.fontSize = '1rem';
      bpText.style.fontWeight = 'bold';
      bpText.style.lineHeight = '2.5';
      bpField.parentNode?.replaceChild(bpText, bpField);
    }
  };
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    setMaxMin: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (value >= 1 && value <= max) {
      setValue(value);
      setMaxMin(false);
    } else if (value >= max) {
      setValue(max);
      setMaxMin(true);
    } else {
      setValue(1);
      setMaxMin(true);
    }
  };

  return (
    <div className="databand-roi widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          dataEngineers={dataEngineers}
          meanTimeToDetect={meanTimeToDetect}
          meanTimeToResolve={meanTimeToResolve}
          hoursSaving={hoursSaving}
          yearlySaving={yearlySaving}
          threeYearSaving={threeYearSaving}
          focusLevel={focusLevel}
          bpName={bpName}
        />
      ) : (
        <div className="bx--grid" id="DatabandGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div
                  className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
                  id="countryModal"
                >
                  {localExist && toggleCountry ? (
                    <ModalWrapper
                      modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                      modalHeading={`The QRadar estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={'Cool! Take me there'}
                      secondaryButtonText={''}
                      buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                      handleOpen={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'QRadar Landing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'SIEM (SaaS) tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                      handleSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/databand-roi/pricing'),
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'QRadar Landing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'SIEM (SaaS) tile',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                    ></ModalWrapper>
                  ) : null}
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 mar2">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_sub_heading mar1">
                        {useTranslation('welcome_msg_1')}
                      </p>
                      <p className="page_body mar3">
                        {useTranslation('scoping_text')}
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'Number of data engineers tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            {useTranslation('triggertext_data_engineers')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_data_engineers')}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                      <NumberInput
                        id="dataEngineers"
                        size="md"
                        allowEmpty={true}
                        ref={dataEngineersRef}
                        className="number-input"
                        min={1}
                        invalid={invalidDataEngineers}
                        invalidText={
                          invalidDataEngineers ? (
                            <span className="slide_text">
                              {useTranslation('invalidTextDataEngineers')}
                            </span>
                          ) : null
                        }
                        max={Max_Data_Engineers}
                        value={dataEngineers}
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'Number of data engineers slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(
                            Number(dataEngineersRef.current.value),
                            setDataEngineers,
                            Max_Data_Engineers,
                            setInvalidDataEngineers,
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'MTTD tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            {useTranslation('triggertext_MTTD')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_MTTD')}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="MTTD"
                        min={1}
                        max={Max_Mean_Time_To_Detect}
                        value={meanTimeToDetect}
                        onBlur={() => {
                          null;
                        }}
                        invalid={invalidMeanTimeToDetect}
                        labelText={
                          invalidMeanTimeToDetect ? (
                            <span className="slide_text">
                              {useTranslation('invalidTextMTTD')}
                            </span>
                          ) : null
                        }
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'MTTD Slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(
                            value,
                            setMeanTimeToDetect,
                            Max_Mean_Time_To_Detect,
                            setInvalidMeanTimeToDetect,
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'MTTR tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            {useTranslation('triggertext_MTTR')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_MTTR')}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="MTTR"
                        min={1}
                        max={Max_Mean_Time_To_Resolve}
                        value={meanTimeToResolve}
                        onBlur={() => {
                          null;
                        }}
                        invalid={invalidMeanTimeToResolve}
                        labelText={
                          invalidMeanTimeToResolve ? (
                            <span className="slide_text">
                              {useTranslation('invalidTextMTTR')}
                            </span>
                          ) : null
                        }
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'MTTR Slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(
                            value,
                            setMeanTimeToResolve,
                            Max_Mean_Time_To_Resolve,
                            setInvalidMeanTimeToResolve,
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row mar3 mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Databand ROI Page',
                            object: 'ROI page',
                            resultValue: 'CTA clicked',
                            CTA: 'Focus level radio button tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            {useTranslation('focus_level_group_name')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_focus_level')}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row mar3 mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <RadioButtonGroup
                        legendText=""
                        name="focusLevelGroup"
                        defaultSelected="radio-1"
                        valueSelected={'Low'}
                        ref={focusRef}
                        onChange={changeSelection}
                      >
                        <RadioButton labelText="Low" value="Low" id="radio-1" />
                        <RadioButton
                          labelText="Medium"
                          value="Medium"
                          id="radio-2"
                        />
                        <RadioButton
                          labelText="High"
                          value="High"
                          id="radio-3"
                        />
                        <RadioButton
                          labelText="None"
                          value="None"
                          id="radio-4"
                        />
                      </RadioButtonGroup>
                    </div>
                  </div>
                  <div id="footnote" className="div-only-lg">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Footnote numNote={1} />
                        <div className="footnote_text">
                          {useTranslation('footnote_one')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                  <div className="bx--row stickyNav">
                    <div className="sticky-nav databand-roi__stickynav_mar price_table_comp">
                      <div>
                        <p
                          id="widget.estimated-cost"
                          className="page_body_2 padding_24_2"
                        >
                          {useTranslation('hours_saved_year')}
                        </p>
                        <p className="price_table_comp__price">{hoursSaving}</p>
                        <p
                          id="widget.estimated-cost"
                          className="page_body_2 padding_24_2"
                        >
                          {useTranslation('yearly_savings')}
                          <sup className="databand-roi__sup">1</sup>
                        </p>
                        <p className="price_table_comp__price">
                          {yearlySaving}
                        </p>
                        <p
                          id="widget.estimated-cost"
                          className="page_body_2 padding_24_2"
                        >
                          {useTranslation('three_year_savings')}
                          <sup className="databand-roi__sup">1</sup>
                        </p>
                        <p className="price_table_comp__price padding_24_1">
                          {threeYearSaving}
                        </p>
                      </div>
                      <ButtonSet className="databand-roi__stack-button" stacked>
                        <Button
                          onClick={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'QRadar Landing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'SIEM (SaaS) tile',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            initUrxForm(
                              environment,
                              URX_FORM.INSTANCE_ID,
                              URX_FORM.FORM_ID,
                              urxlang as IBMLocale,
                              companyId,
                            );
                            replaceBpField();
                            setDrawerOpen(true);
                            setCookie();
                          }}
                        >
                          {useTranslation('request_a_quote')}
                        </Button>
                      </ButtonSet>
                    </div>
                  </div>
                </div>
              </div>
              <div id="footnote" className="div-only-media">
                <div className="bx--row mar6">
                  <div className="bx--col-lg-9">
                    <div>
                      <Footnote numNote={1} />
                    </div>
                    <div className="footnote_text">
                      {useTranslation('footnote_one')}
                    </div>
                  </div>
                </div>
              </div>

              <FormModal
                pageTitle="Databand ROI Page"
                object="Databand ROI Page"
                productTitle={'Databand ROI estimator'}
                pageUrl={''}
                location={'Databand ROI Estimator'}
                bpName={bpName}
                logo={logo}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                formId={URX_FORM.FORM_ID}
                lang={lang}
              ></FormModal>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabandRoiEstimator;
